import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  getLocalDevices,
  handleEvent,
  JazzRoom,
  JazzSdkCreateAnonymousRoomDetails,
  JazzSdkCreateConferenceDetails,
} from '@salutejs/jazz-sdk-web';
import { tertiary } from '@salutejs/plasma-tokens';
import uniqueId from 'lodash/uniqueId';
import styled from 'styled-components/macro';

import { useGlobalContext } from '../../shared/contexts/globalContext';
import { ClientCardComponent } from '../../shared/types/clientCard';

import { ConferenceInfoModal } from './containers/ConferenceInfoModal';
import {
  JoinToConferenceModal,
  JoinToConferenceModalForm,
} from './containers/JoinToConferenceModal';
import { UserProfile } from './containers/UserProfile';

const Wrapper = styled.div`
`;

const Content = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

export const ClientCard: ClientCardComponent = ({
  client,
  roomCardComponent: RoomCard,
}) => {
  const { eventBus } = useGlobalContext();

  const sdk = client.sdk;

  const [rooms, setRooms] = useState<{ key: string; room: JazzRoom }[]>([]);

  const [isOpenJoinToConferenceModal, setIsOpenJoinToConferenceModal] =
    useState(false);
  const [roomDetails, setRoomDetails] = useState<
    | JazzSdkCreateAnonymousRoomDetails
    | JazzSdkCreateConferenceDetails
    | undefined
  >(undefined);

  const localDevices = useMemo(() => {
    return getLocalDevices(sdk);
  }, [sdk]);

  useEffect(() => {
    const unsubscribeAddRoom = handleEvent(
      client.event$,
      'addRoom',
      async ({ payload }) => {
        setRooms((rooms) => [
          ...rooms,
          { key: uniqueId(), room: payload.room },
        ]);
      },
    );

    const unsubscribeRemoveRoom = handleEvent(
      client.event$,
      'removeRoom',
      ({ payload }) => {
        setRooms((rooms) => rooms.filter((room) => room.room !== payload.room));
      },
    );

    return () => {
      unsubscribeAddRoom();
      unsubscribeRemoveRoom();
    };
  }, [client]);

  const handleCloseJoinToConferenceModal = useCallback(() => {
    setIsOpenJoinToConferenceModal(false);
  }, []);

  const handleConnectToConference = useCallback(
    async (form: JoinToConferenceModalForm) => {
      handleCloseJoinToConferenceModal();

      try {
        await client.conferences.getDetails({
          conferenceId: form.roomId,
          password: form.password,
        });
      } catch (error) {
        eventBus({
          type: 'error',
          payload: {
            title: 'Fail connect to conference',
          },
        });
        return;
      }

      const room = client.conferences.join({
        conferenceId: form.roomId,
        password: form.password,
      });

      console.log('Connecting to room', room);

      try {
        const [audioStream, videoStream] = await Promise.all([
          localDevices.getSelectedAudioInputStream(),
          localDevices.getSelectedVideoInputStream(),
        ]);

        console.log('Add mediaStreams to room');

        room.setUserAudioInput(audioStream);
        room.setUserVideoInput(videoStream);

        const releaseMedia = () => {
          console.log('Release mediaStreams');

          localDevices.releaseMediaStream(audioStream);
          localDevices.releaseMediaStream(videoStream);

          const displayStream = room.displayStream.get();

          if (displayStream) {
            localDevices.releaseMediaStream(displayStream);
          }
        };

        handleEvent(room.event$, 'destroy', releaseMedia, true);
      } catch (error) {
        console.log('Media permission denied');
      }
    },
    [client, localDevices, handleCloseJoinToConferenceModal, eventBus],
  );

  const handleJoin = useCallback(() => {
    if (!client.auth.isAuthorised.get()) {
      eventBus({
        type: 'error',
        payload: {
          title: 'To join the conference, you need to sign in',
        },
      });
      return;
    }
    setIsOpenJoinToConferenceModal(true);
  }, [eventBus, client]);

  return (
    <Wrapper>
      <UserProfile client={client} />
      <Content>
        {rooms.map((room) => (
          <RoomCard key={room.key} room={room.room} />
        ))}
      </Content>

      <ConferenceInfoModal
        roomDetails={roomDetails}
        onClose={() => setRoomDetails(undefined)}
      />

      {rooms.length === 0 && (
        <JoinToConferenceModal
          isOpen={isOpenJoinToConferenceModal}
          client={client}
          onClose={handleCloseJoinToConferenceModal}
          onJoin={handleConnectToConference}
        />
      )}
    </Wrapper>
  );
};
